import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Truck, CheckCircle2, CalendarCheck, Package, XCircle, Search } from "lucide-react";
import Seo from "../components/Seo";

const STEPS = [
  { key: "pending",            label: "Pending",            Icon: Package },
  { key: "confirmed",          label: "Confirmed",          Icon: CheckCircle2 },
  { key: "fitting_scheduled",  label: "Fitting Scheduled",  Icon: CalendarCheck },
  { key: "completed",          label: "Completed",          Icon: Truck },
];

const STATUS_LABEL = {
  pending: "Pending",
  confirmed: "Confirmed",
  fitting_scheduled: "Fitting Scheduled",
  completed: "Completed",
  cancelled: "Cancelled",
};

const OrderTrack = () => {
  const [params, setParams] = useSearchParams();
  const initialId = params.get("id") || "";
  const initialPc = params.get("postcode") || "";
  const [form, setForm] = useState({ id: initialId, postcode: initialPc });
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lookup = useCallback(async (id, postcode) => {
    if (!id || !postcode) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orders/track/lookup`, { params: { order_id: id, postcode } });
      setOrder(data);
    } catch (e) {
      setError(e?.response?.status === 404 ? "Order not found — check the reference and postcode." : "Could not look up order.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialId && initialPc) lookup(initialId, initialPc);
  }, [initialId, initialPc, lookup]);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams({ id: form.id, postcode: form.postcode });
    lookup(form.id.trim(), form.postcode.trim());
  };

  const currentIdx = order ? STEPS.findIndex((s) => s.key === order.status) : -1;
  const isCancelled = order?.status === "cancelled";

  return (
    <div className="vd-page" data-testid="page-order-track">
      <Seo title="Track Your Order" description="Track your ValueDrive Auto Solutions tyre order — see status updates and fitting schedule in real time." path="/order/track" />

      <section className="vd-section-secondary py-12">
        <div className="vd-container">
          <h1 className="mb-3">Track Your Order</h1>
          <p className="vd-muted max-w-2xl">Enter your order reference and postcode to see live status updates.</p>

          <form onSubmit={onSubmit} className="vd-card p-5 mt-6 grid grid-cols-1 md:grid-cols-[1.5fr_1fr_auto] gap-3 items-end max-w-3xl" data-testid="order-track-form">
            <div>
              <label className="vd-muted text-xs uppercase tracking-wider">Order reference</label>
              <input className="vd-input mt-1" placeholder="e.g. 8a068efd-a01f-49de-…" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} required data-testid="track-input-id" />
            </div>
            <div>
              <label className="vd-muted text-xs uppercase tracking-wider">Postcode</label>
              <input className="vd-input mt-1" placeholder="TS1 1AA" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} required data-testid="track-input-postcode" />
            </div>
            <button type="submit" disabled={loading} className="vd-btn-primary justify-center" data-testid="track-submit"><Search size={16} /> {loading ? "Looking up…" : "Track"}</button>
          </form>
        </div>
      </section>

      <section className="vd-container py-10">
        {error && <p className="vd-card p-4 text-sm" style={{ color: "var(--primary-red)" }} data-testid="track-error">{error}</p>}

        {order && (
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6" data-testid="track-result">
            <div className="vd-card p-6">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                <div>
                  <p className="vd-muted text-xs uppercase tracking-wider">Order reference</p>
                  <p className="vd-heading font-mono font-bold" style={{ fontSize: 16 }}>{order.id}</p>
                </div>
                <span
                  className="text-xs font-bold rounded-full px-3 py-1"
                  style={{
                    background: isCancelled ? "rgba(230,0,0,0.15)" : "rgba(0,180,80,0.15)",
                    color: isCancelled ? "var(--primary-red)" : "#00A35C",
                  }}
                  data-testid="track-status-badge"
                >{STATUS_LABEL[order.status] || order.status}</span>
              </div>
              <p className="vd-muted text-sm mt-1">Placed {new Date(order.created_at).toLocaleString()}</p>

              {/* Step tracker */}
              {!isCancelled ? (
                <div className="mt-7" data-testid="track-stepper">
                  <div className="flex items-center justify-between gap-2 relative">
                    <div className="absolute left-0 right-0 top-5 h-px" style={{ background: "var(--border-color)", zIndex: 0 }} aria-hidden="true" />
                    <div
                      className="absolute left-0 top-5 h-px transition-all"
                      style={{
                        background: "var(--primary-red)",
                        width: `${Math.max(0, currentIdx) / (STEPS.length - 1) * 100}%`,
                        zIndex: 1,
                      }}
                      aria-hidden="true"
                    />
                    {STEPS.map((s, i) => {
                      const done = currentIdx >= i;
                      const Icon = s.Icon;
                      return (
                        <div key={s.key} className="flex flex-col items-center text-center relative" style={{ zIndex: 2, flex: 1 }} data-testid={`track-step-${s.key}`}>
                          <span
                            className="inline-flex items-center justify-center rounded-full"
                            style={{
                              width: 40, height: 40,
                              background: done ? "var(--primary-red)" : "var(--bg-secondary)",
                              border: `2px solid ${done ? "var(--primary-red)" : "var(--border-color)"}`,
                              color: done ? "#fff" : "var(--text-muted)",
                            }}
                          ><Icon size={18} /></span>
                          <p className="vd-heading font-semibold text-xs mt-2" style={{ color: done ? "var(--text-heading)" : "var(--text-muted)" }}>{s.label}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="mt-6 vd-card p-4 flex items-center gap-3" style={{ borderColor: "var(--primary-red)" }} data-testid="track-cancelled">
                  <XCircle size={22} color="var(--primary-red)" />
                  <p className="vd-heading font-semibold">This order was cancelled.</p>
                </div>
              )}

              {/* History */}
              <div className="mt-7" data-testid="track-history">
                <p className="vd-heading font-semibold mb-3">Status history</p>
                <ul className="space-y-3">
                  {[...(order.status_history || [])].reverse().map((h, i) => (
                    <li key={i} className="flex gap-3" data-testid={`track-history-${i}`}>
                      <span className="inline-flex shrink-0 items-center justify-center rounded-full" style={{ width: 22, height: 22, background: i === 0 ? "var(--primary-red)" : "var(--bg-secondary)", color: i === 0 ? "#fff" : "var(--text-heading)", border: "1px solid var(--border-color)", fontSize: 12, fontWeight: 700 }}>{order.status_history.length - i}</span>
                      <div className="flex-1 border-b pb-3" style={{ borderColor: "var(--border-color)" }}>
                        <p className="vd-heading font-semibold text-sm">{STATUS_LABEL[h.status] || h.status}</p>
                        {h.note && <p className="vd-muted text-sm">{h.note}</p>}
                        <p className="vd-muted text-xs mt-0.5">{new Date(h.timestamp).toLocaleString()}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="vd-card p-6 h-fit" data-testid="track-summary">
              <p className="vd-heading font-semibold mb-3">Order summary</p>
              <Row k="Customer" v={order.full_name} />
              <Row k="Postcode" v={order.postcode} />
              {(order.service_type || "mobile_fitting") === "delivery"
                ? <Row k="Fulfilment" v="Delivery only (no fitting)" />
                : <Row k="Fitting" v={`${order.fitting_date || "—"} · ${order.time_slot || "—"}`} />}
              <div className="border-t my-3" style={{ borderColor: "var(--border-color)" }} />
              <div className="space-y-2 mb-3">
                {order.items.map((i, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm" data-testid={`track-item-${idx}`}>
                    {i.image && <img src={i.image} alt="" className="w-9 h-9 rounded object-cover" style={{ background: "var(--bg-secondary)" }} />}
                    <div className="flex-1 min-w-0"><p className="vd-heading truncate">{i.name}</p><p className="vd-muted text-xs">Qty {i.quantity}</p></div>
                    <p className="vd-heading font-semibold">£{(i.price * i.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between border-t pt-3" style={{ borderColor: "var(--border-color)" }}>
                <span className="vd-heading font-bold">Total</span>
                <span className="font-extrabold" style={{ color: "var(--primary-red)" }} data-testid="track-total">£{order.total.toFixed(2)}</span>
              </div>
              <Link to="/contact" className="vd-btn-secondary justify-center w-full mt-4 text-sm" data-testid="track-contact-link">Need help? Contact us</Link>
            </div>
          </div>
        )}

        {!order && !error && !loading && initialId === "" && (
          <div className="vd-card p-8 text-center" data-testid="track-empty">
            <Truck size={28} color="var(--primary-red)" className="mx-auto mb-3" />
            <p className="vd-heading font-semibold mb-1">Enter your details above to track an order</p>
            <p className="vd-muted text-sm">We sent your order reference by SMS at checkout.</p>
          </div>
        )}
      </section>
    </div>
  );
};

const Row = ({ k, v }) => (
  <div className="py-1.5 text-sm">
    <p className="vd-muted text-xs uppercase tracking-wider">{k}</p>
    <p className="vd-heading font-medium">{v || "—"}</p>
  </div>
);

export default OrderTrack;
